import React, { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowRight2, MessageQuestion, ReceiptItem, Warning2 } from "iconsax-reactjs";
import emptyInvoiceImg from "../../asset/images/emptyinvoice.svg";
import getInitialsMessage from "../../utilities/messages/getInitialsMessage";
import axios from "axios";
import { toast } from "react-toastify";
import Loader from "../loader";
import { safeCurrencyFormat } from "../../utilities/safeCurrencyFormat";
import {
    getOtcRequestId,
    mergeOtcHistoryStatus,
} from "../../utilities/transactionUtils";
import OTCTransactionDetails from "./OTCTransactionDetails";
import {
    CROSS_BORDER_FIELD_SCHEMAS,
    patchCrossBorderTransfer,
    resetCrossBorderTransfer,
} from "../../redux/slices/crossBorderTransferSlice";

const pickDocument = (document, fallbackName) => {
    const url = typeof document === "string" ? document : document?.url;
    if (!url) return null;
    return { url, name: document?.name || fallbackName };
};

const buildResumedRecipient = (txn) => {
    const raw = txn.raw || {};
    const rawRecipient = raw.recipient || raw.otc?.recipient || {};
    const toCurrency = String(txn.otc?.toCurrency || "").toUpperCase();
    const accountName =
        raw.accountName ||
        raw.receiverName ||
        rawRecipient.account_owner_name ||
        rawRecipient.accountName ||
        "";
    const accountNumber =
        raw.accountNumber ||
        rawRecipient.accountNumber ||
        rawRecipient.account?.accountNumber ||
        rawRecipient.account?.account_number ||
        "";
    const bankName =
        raw.bankName || raw.bank_name || rawRecipient.bank_name || rawRecipient.bankName || "";

    if (toCurrency === "USD") {
        return {
            country: raw.location || raw.country || "US",
            bankName,
            toCurrency,
            fields: {},
            usd: {
                bank_name: bankName,
                account_type: raw.account_type || "us",
                paymentRail: raw.paymentTrail || raw.paymentRail || "ach",
                account_owner_name: accountName,
                account: {
                    account_number: accountNumber,
                    routing_number:
                        raw.routingNumber ||
                        rawRecipient.account?.routingNumber ||
                        rawRecipient.account?.routing_number ||
                        "",
                },
            },
        };
    }

    const fields = (CROSS_BORDER_FIELD_SCHEMAS[toCurrency] || []).reduce((result, field) => {
        result[field.key] = raw[field.key] || "";
        return result;
    }, {});
    fields.accountName = fields.accountName || accountName;
    fields.accountNumber = fields.accountNumber || accountNumber;
    fields.bankName = fields.bankName || bankName;

    return {
        country: raw.location || raw.country || "",
        bankName,
        toCurrency,
        fields,
    };
};

export default function TransactionDetails({ txn, typeToIcon }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isDownloading, setIsDownloading] = useState(false);
    const [refreshedOtcTxn, setRefreshedOtcTxn] = useState(null);

    const refreshOtcStatus = useCallback(() => {
        if (!txn?.isOtc) return;
        axios
            .get("/v1/otc/history-status")
            .then(({ data }) => {
                setRefreshedOtcTxn(mergeOtcHistoryStatus(txn, data));
            })
            .catch(() => {});
    }, [txn]);

    useEffect(() => {
        let ignore = false;
        setRefreshedOtcTxn(null);

        if (!txn?.isOtc) return () => {
            ignore = true;
        };

        axios
            .get("/v1/otc/history-status")
            .then(({ data }) => {
                if (!ignore) setRefreshedOtcTxn(mergeOtcHistoryStatus(txn, data));
            })
            .catch(() => {
                // The transaction response contains a history snapshot, which remains
                // visible if the live history refresh is temporarily unavailable.
            });

        return () => {
            ignore = true;
        };
    }, [txn, txn?.id, txn?.isOtc, txn?.otcId]);

    const displayedTxn = refreshedOtcTxn?.id === txn?.id ? refreshedOtcTxn : txn;
    const handleDownloadReceipt = async () => {
        try {
            setIsDownloading(true);
            const response = await axios.get(`/v1/wallets/download/${txn.id}`, {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `receipt-${txn.id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Download failed:", err);
            alert("Failed to download receipt.");
        } finally {
            setIsDownloading(false);
        }
    };

    if (!txn) {
        return (
            <div className="text-center flex-1 py-40 min-h-[91vh] mr-6 mt-4 shadow-lg flex flex-col items-center justify-center">
                <img src={emptyInvoiceImg} alt="No transaction selected" className="w-40 mb-4" />
                <h2 className="text-lg font-semibold text-[#101828]">No transaction selected</h2>
                <p className="text-sm text-[#6F6B7B] mt-2">
                    Choose one from the list to view its full details
                </p>
            </div>
        );
    }

    if (displayedTxn.isOtc) {
        const handleOtcAction = async (action, actionTxn, reason) => {
            let actionTarget = actionTxn;
            let otcId = getOtcRequestId(actionTarget);
            const statusByAction = {
                cancel: "canceled",
                decline: "decline",
            };
            const status = statusByAction[action];

            if (!status) return false;
            if (!otcId) {
                try {
                    const historyResponse = await axios.get("/v1/otc/history-status");
                    actionTarget = mergeOtcHistoryStatus(actionTarget, historyResponse.data);
                    otcId = getOtcRequestId(actionTarget);
                    if (otcId) setRefreshedOtcTxn(actionTarget);
                } catch {
                    // The actionable error is shown below when the ID remains unavailable.
                }
                if (!otcId) {
                    toast.error(
                        "We couldn’t identify this OTC request. Please refresh and try again."
                    );
                    return false;
                }
            }

            try {
                const { data } = await axios.patch(`/v1/otc/${otcId}/status`, {
                    status,
                    ...(reason?.trim() ? { reason: reason.trim() } : {}),
                });
                if (data?.error) {
                    toast.error(data?.message || "Unable to update this OTC request");
                    return false;
                }
                const actionKey = action === "cancel" ? "cancelled" : "offer_declined";
                const message =
                    action === "cancel"
                        ? "You cancelled your OTC request"
                        : "You declined our offer";
                const optimisticTxn = mergeOtcHistoryStatus(actionTarget, {
                    history: [
                        {
                            action: actionKey,
                            status,
                            message,
                            at: new Date().toISOString(),
                        },
                        ...(actionTarget.otc?.history || []),
                    ],
                });
                setRefreshedOtcTxn(mergeOtcHistoryStatus(optimisticTxn, data));

                try {
                    const historyResponse = await axios.get("/v1/otc/history-status");
                    const serverTxn = mergeOtcHistoryStatus(
                        optimisticTxn,
                        historyResponse.data
                    );
                    const expectedPhase = action === "cancel" ? "cancelled" : "declined";
                    if (serverTxn.otcState?.phase === expectedPhase) {
                        setRefreshedOtcTxn(serverTxn);
                    }
                } catch {
                    // Keep the successful optimistic update when history refresh is unavailable.
                }

                toast.success(
                    data?.message ||
                        (action === "cancel"
                            ? "OTC request cancelled successfully"
                            : "Offer declined successfully")
                );
                return true;
            } catch (error) {
                toast.error(
                    error?.response?.data?.message ||
                        (action === "cancel"
                            ? "Unable to cancel this request"
                            : "Unable to decline this offer")
                );
                return false;
            }
        };

        const handleAcceptOffer = () => {
            const raw = displayedTxn.raw || {};
            const otc = displayedTxn.otc || {};
            const recipient = buildResumedRecipient(displayedTxn);
            const hasRecipientDetails = Boolean(
                String(recipient?.usd?.account?.account_number || recipient?.fields?.accountNumber || "").trim() &&
                    String(recipient?.usd?.account_owner_name || recipient?.fields?.accountName || "").trim()
            );

            dispatch(resetCrossBorderTransfer());
            dispatch(
                patchCrossBorderTransfer({
                    fromCurrency: otc.fromCurrency,
                    amount: String(Math.abs(Number(displayedTxn.amount || otc.amount || 0))),
                    fee: Number(raw.transactionFee ?? raw.fee ?? 0),
                    rate: Number(otc.standardRate || 0),
                    rateKey: raw.rateKey || "",
                    isOTC: true,
                    preferredRate: String(otc.offeredRate ?? otc.preferredRate ?? ""),
                    otcNotes: otc.note || "",
                    usePreferredRate: true,
                    recipientSkipped: false,
                    otcRequestId: displayedTxn.otcId || otc._id || "",
                    isResumingOtcOffer: true,
                    memo: otc.narration || raw.narration || "",
                    invoice: pickDocument(raw.invoice, "Invoice"),
                    otherDocument: pickDocument(
                        raw.supportDocument || raw.supportingDocument,
                        "Supporting document"
                    ),
                    recipient,
                })
            );

            navigate(
                hasRecipientDetails
                    ? "/payment/send-money/cross-border/bank-recipient/review-payment-details"
                    : "/payment/send-money/cross-border/bank-recipient/recipient-info"
            );
        };

        return (
            <OTCTransactionDetails
                txn={displayedTxn}
                onAcceptOffer={handleAcceptOffer}
                onOtcAction={handleOtcAction}
                onExpire={refreshOtcStatus}
                footer={
                    <div className="mt-auto pt-4 border-t sticky bottom-0 bg-white z-10">
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={!isDownloading ? handleDownloadReceipt : undefined}
                                disabled={isDownloading}
                                className="w-full py-3 bg-white border border-[#EAECF0] rounded-lg flex items-center justify-center gap-2 text-[12px] text-[#6F739B] disabled:opacity-60"
                            >
                                {isDownloading ? (
                                    <Loader size="sm" />
                                ) : (
                                    <>
                                        <ReceiptItem size="16" color="#6F739B" variant="Bulk" />
                                        Download receipt
                                    </>
                                )}
                            </button>
                            <button
                                disabled
                                className="w-full py-3 bg-white border border-[#EAECF0] rounded-lg flex items-center justify-center gap-2 text-[12px] text-[#6F739B] opacity-60 cursor-not-allowed"
                            >
                                <Warning2 size="16" color="#6F739B" variant="Bulk" />
                                Report an issue
                            </button>
                        </div>
                    </div>
                }
            />
        );
    }

    const validDetails = (Array.isArray(txn.details) ? txn.details : []).filter(
        (detail) => {
            const value = detail?.value;
            if (React.isValidElement(value)) return true;
            if (value === null || value === undefined) return false;
            const normalizedValue = String(value).trim();
            return normalizedValue !== "" && normalizedValue !== "—";
        }
    );

    return (
      <div className="flex flex-col p-6 bg-white rounded-tl-xl h-[91vh] md:mt-4 md:mr-6 rounded-tr-xl overflow-y-auto">
            {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-6">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 shrink-0 bg-[#F2F3FC] rounded-full flex items-center justify-center text-[#383B56] font-[450] text-[16px]">
                        {txn.transactionType === "BalanceSwap"
                            ? typeToIcon[txn.transactionType] || (
                                <MessageQuestion size="18" color="#6F739B" variant="Bulk" />
                            )
                            : getInitialsMessage(txn?.receiverName || txn?.title)}
                    </div>

            <div className="min-w-0 flex-1">
              <h2
                className="text-[16px] truncate whitespace-nowrap font-[450] mb-0 text-[#101828]"
                title={txn.title}
              >
                            {txn.title}
                        </h2>
              <p className="text-[14px] font-[350] text-[#6F739B] whitespace-nowrap">
                            {dayjs(txn.time).format("MMMM D, YYYY • h:mm A")}
                        </p>
                    </div>
                </div>
          <div className="shrink-0 text-end whitespace-nowrap">
                    <p
              className={`text-[20px] text-end font-[450] whitespace-nowrap ${txn.amount >= 0 ? "text-[#11A811]" : "text-[#161827]"
                }`}
                    >
              {safeCurrencyFormat(txn.amount, txn.currency, { compactThreshold: 100000 })}
                    </p>
                    <p
              className={`text-[14px] text-end font-medium ${txn.status === "Completed"
                                ? "text-[#188B18]"
                                : txn.status === "Pending" || txn.status === "Processing"
                                    ? "text-orange-500"
                                    : "text-red-500"
                }`}
                    >
                        {txn.status}
                    </p>
                </div>
            </div>

        {/* Payment Details */}
        {((txn.status === "Failed" && txn?.reasonForFailure) || validDetails.length > 0) && (
          <>
            <h3 className="text-[12px] font-[500] text-[#6F739B] uppercase mb-2">
              Payment Details
            </h3>
            {/* Details Table */}
            <div className="bg-[#F7F8FD] !p-5 rounded-lg mb-6">
              {txn.status === "Failed" && txn?.reasonForFailure && (
                <dl
                  style={{
                    borderBottom: validDetails.length > 0 ? "1px solid #E5E7EB" : "none",
                  }}
                  className={`grid grid-cols-2 gap-y-3 ${validDetails.length > 0 ? "mb-2 pb-2" : ""}`}
                >
                  <dt className="text-[14px] font-[350] text-[#6F739B]">Reason</dt>
                  <dd className="text-[14px] text-[#101828] text-end break-words break-all whitespace-pre-wrap">
                    {txn?.reasonForFailure}
                  </dd>
                </dl>
              )}
              {validDetails.length > 0 && (
                <dl className="grid grid-cols-2 gap-y-3">
                  {validDetails.map(({ label, value }) => (
                    <React.Fragment key={label}>
                      <dt className="text-[14px] font-[350] text-[#6F739B]">{label}</dt>
                      <dd className="text-[14px] text-[#101828] text-end break-words break-all whitespace-pre-wrap">
                                      {value}
                                    </dd>
                                  </React.Fragment>
                                ))}
                </dl>
              )}
            </div>
          </>
        )}
            {/* Attachments */}
            {txn.attachments?.length > 0 && (
                <div className="rounded-lg mb-6">
                    <h3 className="text-sm text-[#6F739B] text-[12px] font-[500] uppercase mb-2">
                        Attachments
                    </h3>
                    <ul className="divide-y divide-gray-200 !px-5 !py-2 border ">
                        {txn.attachments.map((att) => (
                            <li key={att.id} className="py-3 flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <ReceiptItem size="18" color="#6F739B" variant="Bulk" />
                                    <span className="text-sm text-[#101828]">{att.label}</span>
                                </div>
                                <a
                                    href={att.url}
                                    className="flex items-center gap-1 text-sm text-[#6F00FF]"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span>
                                        {att.fileName} {att.fileSize ? ` — ${att.fileSize}` : ""}
                                    </span>
                                    <ArrowRight2 size={16} />
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {/* Sticky bottom actions */}
            <div className="mt-auto pt-4 border-t sticky bottom-0 bg-white z-10">
                <div className="md:flex grid gap-2 md:gap-4">
                    <div className="flex-1 group relative">
                        {txn.transactionType !== "TransactionFee" && (
                            <button
                                onClick={!isDownloading ? handleDownloadReceipt : undefined}
                                disabled={isDownloading}
                                className={`w-full py-3 bg-white border border-[#F2F3FC] rounded-lg flex items-center justify-center gap-2
                          ${isDownloading ? "opacity-60 cursor-not-allowed" : "hover:bg-[#F7F8FD]"
                                    }`}
                            >
                                {isDownloading ? (
                                    <Loader size="sm" />
                                ) : (
                                    <>
                                        <ReceiptItem size="18" color="#6F739B" variant="Bulk" />
                                        Download receipt
                                    </>
                                )}
                            </button>
                        )}
                        <span className="absolute left-1/2 -top-8 -translate-x-1/2 bg-[#101828] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                            Coming soon
                        </span>
                    </div>
                    <div className="flex-1 group relative">
                        <button
                            disabled
                            className="w-full py-3 bg-white border border-gray-200 rounded-lg flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
                        >
                            <Warning2 size="18" color="#6F739B" variant="Bulk" />
                            Report an issue
                        </button>
                        <span className="absolute left-1/2 -top-8 -translate-x-1/2 bg-[#101828] text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition">
                            Coming soon
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
